// serialPortContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Create the context
const SerialPortContext = createContext(null);

// Serial port settings
const DEFAULT_BAUD_RATE = 9600;
const STORAGE_KEY = 'SERIAL_PORT_ID';

export function SerialPortProvider({ children }) {
  const [port, setPort] = useState(null);
  const [portList, setPortList] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [receivedData, setReceivedData] = useState('');
  // const [buffer, setBuffer] = useState('');
  const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);
  
  // Reader and writer references
  const readerRef = useRef(null);
  const writerRef = useRef(null);
  
  // Keep track if we're currently reading
  const keepReadingRef = useRef(false);

  // Use refs for callback registry to prevent infinite loops
  const callbacksRef = useRef({
    onData: [],
    onConnected: [],
    onDisconnected: [],
    onError: []
  });

  // Register event callbacks - now uses ref
  const addEventListener = useCallback((eventType, callback) => {
    if (!callbacksRef.current[eventType]) return false;
    
    callbacksRef.current[eventType].push(callback);
    
    // Return function to remove the event listener
    return () => {
      callbacksRef.current[eventType] = callbacksRef.current[eventType].filter(cb => cb !== callback);
    };
  }, []);

  // Trigger events - now uses ref
  const triggerEvent = useCallback((eventType, data) => {
    if (!callbacksRef.current[eventType]) return;
    
    callbacksRef.current[eventType].forEach(callback => {
      try {
        callback(data);
      } catch (err) {
        console.error(`Error in ${eventType} callback:`, err);
      }
    });
  }, []);

  // Check if Web Serial API is available
  const isSerialSupported = useCallback(() => {
    return 'serial' in navigator;
  }, []);

  // Save port ID to local storage for future auto-connect
  const savePortIdentifier = useCallback((selectedPort) => {
    if (!selectedPort || !selectedPort.getInfo) return;
    
    try {
      const portInfo = selectedPort.getInfo();
      // Store any identifying information available
      const portIdentifier = {
        usbVendorId: portInfo.usbVendorId,
        usbProductId: portInfo.usbProductId
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(portIdentifier));
    } catch (err) {
      console.warn('Failed to save port identifier:', err);
    }
  }, []);
  
  // Find a port by its identifier
  const findPortByIdentifier = useCallback((ports, identifier) => {
    if (!ports || !ports.length || !identifier) return null;
    
    // Try to find a matching port by vendor and product IDs
    return ports.find(port => {
      try {
        const info = port.getInfo();
        return info.usbVendorId === identifier.usbVendorId && 
               info.usbProductId === identifier.usbProductId;
      } catch (err) {
        return false;
      }
    });
  }, []);

  // Get available ports
  const getPorts = useCallback(async () => {
    if (!isSerialSupported()) {
      setError('Web Serial API is not supported in this browser');
      return [];
    }

    try {
      const ports = await navigator.serial.getPorts();
      setPortList(ports);
      return ports;
    } catch (err) {
      setError(`Failed to get ports: ${err.message}`);
      triggerEvent('onError', err);
      return [];
    }
  }, [isSerialSupported, triggerEvent]);

  // Request a port
  const requestPort = useCallback(async (filters = []) => {
    if (!isSerialSupported()) {
      setError('Web Serial API is not supported in this browser');
      return null;
    }

    try {
      // Can pass filters to narrow down device selection
      const port = await navigator.serial.requestPort({ filters });
      setPort(port);
      savePortIdentifier(port);
      await getPorts(); // Refresh port list
      return port;
    } catch (err) {
      // User cancelled or error occurred
      if (err.message !== 'No port selected by the user.') {
        setError(`Failed to request port: ${err.message}`);
        triggerEvent('onError', err);
      }
      return null;
    }
  }, [isSerialSupported, getPorts, triggerEvent, savePortIdentifier]);

  // Read data loop - separated from useEffect
  const startReading = useCallback(async (serialPort) => {
    if (!serialPort) return;
    
    try {
      const portReader = serialPort.readable.getReader();
      readerRef.current = portReader;
      
      while (keepReadingRef.current) {
        const { value, done } = await portReader.read();
        
        if (done) {
          // Reader has been canceled
          break;
        }
        
        if (value) {
          // Process the received data
          // setReceivedData(prev => [...prev, value]);
          const decoder = new TextDecoder();
          const decoded = decoder.decode(value);
          setReceivedData(prev => prev + decoded);
          triggerEvent('onData', value);
        }
      }
      
      // Release the lock when done
      portReader.releaseLock();
      readerRef.current = null;
    } catch (err) {
      setError(`Error reading data: ${err.message}`);
      triggerEvent('onError', err);
      setIsConnected(false);
    }
  }, [triggerEvent]);

  // Disconnect from port
  const disconnect = useCallback(async () => {
    if (!port) return false;
    
    try {
      keepReadingRef.current = false;
      
      // Close reader if it exists
      if (readerRef.current) {
        try {
          await readerRef.current.cancel();
          readerRef.current.releaseLock();
        } catch (err) {
          console.warn('Error releasing reader lock:', err);
        }
        readerRef.current = null;
      }
      
      // Close writer if it exists
      if (writerRef.current) {
        try {
          writerRef.current.releaseLock();
        } catch (err) {
          console.warn('Error releasing writer lock:', err);
        }
        writerRef.current = null;
      }
      
      // Close the port
      if (port.readable || port.writable) {
        await port.close();
      }
      
      setIsConnected(false);
      triggerEvent('onDisconnected', port);
      return true;
    } catch (err) {
      setError(`Failed to disconnect: ${err.message}`);
      triggerEvent('onError', err);
      return false;
    }
  }, [port, triggerEvent]);

  // Connect to a port
  const connect = useCallback(async (selectedPort = null, options = { baudRate: DEFAULT_BAUD_RATE }) => {
    try {
      // Use provided port or the stored one
      const portToUse = selectedPort || port;
      
      if (!portToUse) {
        throw new Error('No port selected');
      }

      if (isConnected) {
        await disconnect();
      }

      await portToUse.open(options);
      setPort(portToUse);
      savePortIdentifier(portToUse);
      setIsConnected(true);
      
      // Set up writer
      const writableStreamClosed = portToUse.writable.getWriter();
      writerRef.current = writableStreamClosed;
      
      // Set up reader and start reading
      keepReadingRef.current = true;
      triggerEvent('onConnected', portToUse);
      
      // Start the reading loop
      startReading(portToUse);
      
      return true;
    } catch (err) {
      setIsConnected(false);
      setError(`Failed to connect: ${err.message}`);
      triggerEvent('onError', err);
      return false;
    }
  }, [port, isConnected, disconnect, triggerEvent, savePortIdentifier, startReading]);

  // Auto-connect to last used port
  const autoConnect = useCallback(async (options = { baudRate: DEFAULT_BAUD_RATE }) => {
    try {
      // Get stored port identifier
      const savedIdentifierString = localStorage.getItem(STORAGE_KEY);
      if (!savedIdentifierString) return false;
      
      const savedIdentifier = JSON.parse(savedIdentifierString);
      
      // Get available ports
      const ports = await getPorts();
      
      // Find the matching port
      const matchedPort = findPortByIdentifier(ports, savedIdentifier);
      if (!matchedPort) return false;
      
      // Connect to the port
      const success = await connect(matchedPort, options);
      return success;
    } catch (err) {
      console.warn('Auto-connect failed:', err);
      return false;
    }
  }, [getPorts, findPortByIdentifier, connect]);

  // Write data to port
  const write = useCallback(async (data) => {
    if (!port || !isConnected || !writerRef.current) {
      return false;
    }
    
    try {
      // Convert data to Uint8Array if it's not already
      let dataToWrite;
      if (data instanceof Uint8Array) {
        dataToWrite = data;
      } else if (typeof data === 'string') {
        const encoder = new TextEncoder();
        dataToWrite = encoder.encode(data);
      } else {
        throw new Error('Data must be a string or Uint8Array');
      }
      
      await writerRef.current.write(dataToWrite);
      return true;
    } catch (err) {
      setError(`Failed to write data: ${err.message}`);
      triggerEvent('onError', err);
      return false;
    }
  }, [port, isConnected, triggerEvent]);

  // Connect to specific device by vendor/product ID
  const connectToDevice = useCallback(async (vendorId, productId, options = { baudRate: DEFAULT_BAUD_RATE }) => {
    try {
      // Get available ports
      const ports = await getPorts();
      
      // Find matching port
      const matchedPort = ports.find(port => {
        const info = port.getInfo();
        return info.usbVendorId === vendorId && info.usbProductId === productId;
      });
      
      if (!matchedPort) {
        // If no pre-authorized port is found, request one with filters
        const requestedPort = await requestPort([{ usbVendorId: vendorId, usbProductId: productId }]);
        if (!requestedPort) return false;
        
        return await connect(requestedPort, options);
      }
      
      // Connect to matched port
      return await connect(matchedPort, options);
    } catch (err) {
      setError(`Failed to connect to specific device: ${err.message}`);
      triggerEvent('onError', err);
      return false;
    }
  }, [getPorts, requestPort, connect, triggerEvent]);

  // Clear received data
  const clearReceivedData = useCallback(() => {
    setReceivedData([]);
  }, []);

  // Init effect - get initial ports and set up listeners
  useEffect(() => {
    const connectedListener = () => {
      getPorts();
    };
    
    const disconnectedListener = (event) => {
      getPorts();
      // If our connected port was disconnected, update state
      if (port && event.target === port) {
        setIsConnected(false);
        setPort(null);
        triggerEvent('onDisconnected', event.target);
      }
    };
    
    if (isSerialSupported()) {
      getPorts();
      
      // Listen for connect/disconnect events
      navigator.serial.addEventListener('connect', connectedListener);
      navigator.serial.addEventListener('disconnect', disconnectedListener);
      
      // Clean up event listeners
      return () => {
        navigator.serial.removeEventListener('connect', connectedListener);
        navigator.serial.removeEventListener('disconnect', disconnectedListener);
      };
    }
  }, [isSerialSupported, getPorts, port, triggerEvent]);

  // Auto-connect on initial load
  useEffect(() => {
    if (isSerialSupported() && !autoConnectAttempted && !isConnected) {
      setAutoConnectAttempted(true);
      autoConnect().then(success => {
        if (!success) {
          console.log('No previously connected ports available');
        }
      });
    }
  }, [isSerialSupported, autoConnect, autoConnectAttempted, isConnected]);

  // Context value - memoize to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    port,
    portList,
    isConnected,
    isSerialSupported: isSerialSupported(),
    error,
    receivedData,setReceivedData,
    
    // Methods
    requestPort,
    getPorts,
    connect,
    disconnect,
    write,
    clearReceivedData,
    autoConnect,
    connectToDevice,
    
    // Event registration
    addEventListener,
    
    // Event types (for reference)
    eventTypes: {
      DATA: 'onData',
      CONNECTED: 'onConnected',
      DISCONNECTED: 'onDisconnected',
      ERROR: 'onError'
    }
  }), [
    port, 
    portList, 
    isConnected, 
    error, 
    receivedData, setReceivedData,
    requestPort, 
    getPorts, 
    connect, 
    disconnect, 
    write, 
    clearReceivedData, 
    autoConnect,
    connectToDevice,
    addEventListener
  ]);

  return (
    <SerialPortContext.Provider value={value}>
      {children}
    </SerialPortContext.Provider>
  );
}

// Custom hook to use the SerialPort context
export function useSerialPort() {
  const context = useContext(SerialPortContext);
  
  if (context === null) {
    throw new Error('useSerialPort must be used within a SerialPortProvider');
  }
  
  return context;
}